import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, Form } from 'react-bootstrap';
import { AiOutlineFileAdd } from "react-icons/ai";
import { BsPencilFill } from "react-icons/bs";
import { LuFilter, LuX } from 'react-icons/lu';
import { FaSortUp, FaSortDown, FaBox, FaBoxOpen, FaRegHandPaper } from "react-icons/fa";
import { GrAttachment } from "react-icons/gr";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { IoSettingsOutline } from "react-icons/io5";
import axios from 'axios';
import Header from '../../../header/header.js';
import Navvbar from '../../../header/Navvbar.js';
import { useCurrency } from '../../../../context/currencyContext';
import NotesSection from '../../../NotesSection.js';
import FileUploadModal from '../../../FileUploadModal.js';

import Swal from 'sweetalert2';

// Pricing access hook — hides amounts when the user doesn't have access
import usePricingAccess from '../../../../hooks/usePricingAccess.js';

import './casesOpened.css';

const API_URL =
  process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

// =====================================================
// TOAST STYLES (presentable SweetAlert toasts)
// =====================================================

const TOAST_CSS = `
  .swal2-container.cases-toast-container {
    padding-top: 84px !important;
    padding-right: 20px !important;
    z-index: 20000 !important;
  }
  .swal2-popup.swal2-toast.cases-toast {
    width: auto !important;
    min-width: 300px;
    max-width: 380px;
    padding: 14px 18px !important;
    border-radius: 10px !important;
    border-left: 5px solid var(--toast-accent, #004aad);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
    align-items: center !important;
    background: #ffffff !important;
  }
  .swal2-popup.swal2-toast.cases-toast .swal2-icon {
    margin: 0 12px 0 0 !important;
    width: 2em;
    height: 2em;
    min-width: 2em;
  }
  .swal2-popup.swal2-toast.cases-toast .swal2-html-container {
    margin: 0 !important;
    padding: 0 !important;
    text-align: left;
  }
  .swal2-popup.swal2-toast.cases-toast .cases-toast-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 2px;
  }
  .swal2-popup.swal2-toast.cases-toast .cases-toast-text {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.35;
  }
  .swal2-popup.swal2-toast.cases-toast .swal2-timer-progress-bar {
    background: var(--toast-accent, #004aad);
  }
  .cases-table-wrapper.is-refreshing {
    opacity: 0.55;
    pointer-events: none;
    transition: opacity 0.15s ease;
  }
`;

// =====================================================
// GET USER LAB ID
// =====================================================

const getUserLabId = () => {
  try {
    const possibleKeys = [
      'user',
      'userData',
      'auth',
      'currentUser',
      'labUser',
      'labUserData'
    ];

    for (const key of possibleKeys) {
      const storedData = localStorage.getItem(key);

      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);

          const userLabId =
            parsed.userLabId ||
            parsed.labId ||
            parsed.id ||
            (parsed.data && parsed.data.userLabId) ||
            (parsed.data && parsed.data.labId) ||
            (parsed.user && parsed.user.userLabId) ||
            (parsed.user && parsed.user.labId);

          if (userLabId) {
            return userLabId;
          }

          if (
            typeof storedData === 'string' &&
            storedData.match(/^000000\d{2}$/)
          ) {
            return storedData;
          }
        } catch (e) {
          continue;
        }
      }
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);

      if (value && value.match(/^000000\d{2}$/)) {
        return value;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
};

// =====================================================
// SORT HELPER — OLDEST FIRST (ascending by date)
// =====================================================

const sortCasesAscending = (casesArray) => {
  return [...casesArray].sort((a, b) => {
    const dateA = new Date(
      a.date || a.start_date || a.delivery_date || 0
    ).getTime();

    const dateB = new Date(
      b.date || b.start_date || b.delivery_date || 0
    ).getTime();

    if (isNaN(dateA) && isNaN(dateB)) return 0;
    if (isNaN(dateA)) return 1;
    if (isNaN(dateB)) return -1;

    return dateA - dateB;
  });
};

// =====================================================
// HELPER: Get month date range as YYYY-MM-DD strings
// =====================================================

const getMonthRange = (year, month) => {
  const startOfMonth = new Date(year, month, 1);
  const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59, 999);

  const formatLocalYMD = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  return {
    startDate: formatLocalYMD(startOfMonth),
    endDate: formatLocalYMD(endOfMonth)
  };
};

// Short display date like "1 Oct 2026"
const formatShortDate = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// =====================================================
// COLUMN SORT HELPERS (sorts the cases already loaded)
// =====================================================

const getSortValue = (c, key) => {
  switch (key) {
    case 'status':
      return isCaseOnHold(c) ? 2 : c.invoiceNumber ? 1 : 0;
    case 'caseNumber':
      return Number(c.caseNumber) || String(c.caseNumber || '');
    case 'invoice':
      return Number(c.invoiceNumber) || '';
    case 'account':
      return c.account_name || c.account_holder || '';
    case 'provider':
      return c.provider_name || '';
    case 'patient':
      return c.last_name || '';
    case 'accountShip':
      return c.start_date ? new Date(c.start_date).getTime() : '';
    case 'labShip':
      return c.date ? new Date(c.date).getTime() : '';
    case 'delivery':
      return c.delivery_date ? new Date(c.delivery_date).getTime() : '';
    default:
      return '';
  }
};

const sortByColumn = (list, sortConfig) => {
  if (!sortConfig.key) return list;

  const dir = sortConfig.dir === 'desc' ? -1 : 1;

  return [...list].sort((a, b) => {
    const va = getSortValue(a, sortConfig.key);
    const vb = getSortValue(b, sortConfig.key);

    if (va === '' && vb === '') return 0;
    if (va === '') return 1;
    if (vb === '') return -1;

    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * dir;
    }

    return (
      String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir
    );
  });
};

const SortHeader = ({ label, colKey, sortConfig, onSort }) => {
  const isActive = sortConfig.key === colKey;

  return (
    <th className="sortable-th" onClick={() => onSort(colKey)}>
      {label}
      <span className="sort-arrows">
        <FaSortUp
          className={`sort-icon${isActive && sortConfig.dir === 'asc' ? ' active' : ''}`}
        />
        <FaSortDown
          className={`sort-icon${isActive && sortConfig.dir === 'desc' ? ' active' : ''}`}
        />
      </span>
    </th>
  );
};

// =====================================================
// CASE STATUS ICON HELPERS
//   FA icons styled to match the reference look:
//     - on hold   -> FaRegHandPaper (filled black)
//     - invoiced  -> FaBox (white fill + black stroke)
//     - open      -> FaBoxOpen (white fill + black stroke)
// =====================================================

const isCaseOnHold = (caseItem) =>
  /hold/i.test(String(caseItem.status || caseItem.caseStatus || ''));

const getStatusIcon = (caseItem) => {
  if (isCaseOnHold(caseItem)) {
    return {
      Icon: FaRegHandPaper,
      className: 'icon-inbox icon-hold',
      tip: caseItem.status || 'On Hold'
    };
  }

  if (caseItem.invoiceNumber) {
    return {
      Icon: FaBox,
      className: 'icon-inbox icon-closed-box',
      tip: 'Closed - Invoiced'
    };
  }

  return {
    Icon: FaBoxOpen,
    className: 'icon-inbox icon-open-box',
    tip: caseItem.status || 'Open'
  };
};

const DEFAULT_FILTERS = {
  filterType: 'account',
  filterValue: '',
  by: 'month',
  caseTag: 'any',
  properties: 'all',
  startDate: '',
  endDate: '',
  caseStatus: ''
};

// =====================================================
// MERGE HELPERS
// =====================================================

const mergeOptions = (prevArr, newArr) => {
  const map = new Map();
  prevArr.forEach((i) => map.set(i.value, i));
  newArr.forEach((i) => map.set(i.value, i));
  return Array.from(map.values()).sort((a, b) =>
    String(a.label).localeCompare(String(b.label))
  );
};

const mergeStrings = (a, b) => Array.from(new Set([...a, ...b])).sort();

// =====================================================
// CASES OPENED COMPONENT
// =====================================================

const CasesOpened = () => {
  const { formatAmount } = useCurrency();

  const { hasPricingAccess, loading: pricingLoading } = usePricingAccess();

  const formatAmountWithCommas = (amount) => {
    if (!hasPricingAccess) return null;

    if (typeof amount !== 'number' || isNaN(amount)) return null;

    const formatted = formatAmount(amount);

    if (typeof formatted !== 'string') return formatted;

    return formatted.replace(/\d+(\.\d+)?/g, (match) => {
      const parts = match.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    });
  };

  const [cases, setCases] = useState([]);
  const [accounts] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(null);

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const [showFilterModal, setShowFilterModal] = useState(false);

  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [monthlyInvoicedTotal, setMonthlyInvoicedTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { accountId, accountName } = location.state || {};

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  const [uploadingStatus, setUploadingStatus] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState({});

  // ---------------------------------------------------
  // PAGINATION STATE
  // ---------------------------------------------------
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [serverTotalAmount, setServerTotalAmount] = useState(0);

  const dropdownRef = useRef({});
  const dropdownMenuRef = useRef({});

  // ---------------------------------------------------
  // FILTER OPTIONS STATE
  // ---------------------------------------------------
  const [filterOptions, setFilterOptions] = useState({
    accounts: [],
    providers: [],
    patients: [],
    caseIds: [],
    deliveryDates: [],
    caseTags: [],
    statuses: []
  });

  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);

  // ---------------------------------------------------
  // COLUMN SORT STATE (click header arrows)
  // ---------------------------------------------------
  const [sortConfig, setSortConfig] = useState({ key: '', dir: 'asc' });

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  // =====================================================
  // SWEET ALERT HELPERS
  // =====================================================

  const fireToast = (icon, title, message, timer, accent) => {
    Swal.fire({
      icon: icon,
      html: `
        <div class="cases-toast-title">${title}</div>
        <div class="cases-toast-text">${message || ''}</div>
      `,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: timer,
      timerProgressBar: true,
      iconColor: accent,
      customClass: {
        container: 'cases-toast-container',
        popup: 'cases-toast'
      },
      didOpen: (toast) => {
        toast.style.setProperty('--toast-accent', accent);
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  };

  const showSuccessAlert = (title, message) => {
    fireToast('success', title, message, 2500, '#004aad');
  };

  const showErrorAlert = (title, message) => {
    fireToast('error', title, message, 4000, '#dc3545');
  };

  const showInfoAlert = (title, message) => {
    fireToast('info', title, message, 2000, '#17a2b8');
  };

  // =====================================================
  // FILE UPLOAD
  // =====================================================

  const fileUploadHelper = FileUploadModal({
    onUploadStart: (caseNumber) => {
      setUploadingStatus((prev) => ({ ...prev, [caseNumber]: true }));
      setUploadSuccess((prev) => ({ ...prev, [caseNumber]: false }));
      showInfoAlert('Uploading', `Uploading files for case #${caseNumber}...`);
    },

    onUploadSuccess: async (caseNumber, data) => {
      setUploadingStatus((prev) => ({ ...prev, [caseNumber]: false }));
      setUploadSuccess((prev) => ({ ...prev, [caseNumber]: true }));

      const uploadedFiles = data.files ? data.files.length : 0;

      showSuccessAlert(
        'Upload Successful!',
        `${uploadedFiles} file(s) uploaded successfully for case #${caseNumber}`
      );
    },

    onUploadError: (caseNumber, errorMessage) => {
      setUploadingStatus((prev) => ({ ...prev, [caseNumber]: false }));
      showErrorAlert(
        'Upload Failed',
        `Failed to upload files for case #${caseNumber}: ${errorMessage}`
      );
    }
  });

  const handleUploadClick = (caseNumber, event) => {
    event.stopPropagation();
    fileUploadHelper.openFileExplorer(caseNumber);
    setDropdownVisible(null);
  };

  // =====================================================
  // FILTERS
  // =====================================================

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [draftFilters, setDraftFilters] = useState({ ...DEFAULT_FILTERS });

  // =====================================================
  // FORMAT HELPERS
  // =====================================================

  const formatInvoiceNumber = (number) => {
    return number ? String(number).padStart(6, '0') : 'N/A';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  // =====================================================
  // OUTSOURCED STATUS
  // =====================================================

  const isCaseOutsourced = (caseItem) => {
    return (
      caseItem.outsourcedTo &&
      Array.isArray(caseItem.outsourcedTo) &&
      caseItem.outsourcedTo.length > 0
    );
  };

  const getOutsourcedStatus = (caseItem) => {
    if (!isCaseOutsourced(caseItem)) return null;

    const outsourcedData = caseItem.outsourcedTo[0];
    const status = outsourcedData.status || 'pending';
    const labName =
      outsourcedData.destinationLab?.labName || 'Unknown Lab';

    return {
      status: status,
      labName: labName,
      displayText: `Outsourced to ${labName}`,
      statusColor:
        status === 'completed'
          ? '#28a745'
          : status === 'pending'
          ? '#ffc107'
          : status === 'in-progress'
          ? '#17a2b8'
          : '#6c757d'
    };
  };

  // =====================================================
  // BUILD QUERY PARAMS FOR API
  // =====================================================

  const buildQueryParams = () => {
    const params = {
      userLabId: getUserLabId(),
      page: currentPage,
      limit: itemsPerPage,
      sortBy: 'date',
      sortOrder: 'asc',
      dateField: 'date'
    };

    if (accountId) params.accountId = accountId;

    if (filters.by === 'month') {
      const { startDate, endDate } = getMonthRange(currentYear, currentMonth);
      params.startDate = startDate;
      params.endDate = endDate;
      params.dateField = 'start_date';
    } else if (filters.by === 'dateRange') {
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      params.dateField = 'date';
    }

    if (filters.filterValue) {
      switch (filters.filterType) {
        case 'account':
          params.accountName = filters.filterValue;
          break;
        case 'provider':
          params.providerName = filters.filterValue;
          break;
        case 'patient':
          params.patientName = filters.filterValue;
          break;
        case 'caseId':
          params.caseNumber = filters.filterValue;
          break;
        case 'delivery':
          params.deliveryDate = filters.filterValue;
          break;
        default:
          break;
      }
    }

    if (filters.caseTag !== 'any') params.caseTag = filters.caseTag;
    if (filters.caseStatus) params.caseStatus = filters.caseStatus;
    if (filters.properties !== 'all') params.properties = filters.properties;

    return params;
  };

  // =====================================================
  // FETCH CASES (SERVER-SIDE PAGINATION)
  // =====================================================

  const fetchCases = async () => {
    try {
      setLoading(true);
      setError(null);

      const userLabId = getUserLabId();

      if (!userLabId) {
        setError('User Lab ID not found. Please login again.');
        setLoading(false);
        return;
      }

      const queryParams = buildQueryParams();

      const response = await axios.get(`${API_URL}/api/cases/lab-cases`, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
        params: queryParams
      });

      if (response.data.success) {
        const casesData = response.data.data || [];
        const sortedCases = sortCasesAscending(casesData);

        setCases(sortedCases);

        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
        setServerTotalAmount(response.data.totalAmount || 0);
      } else {
        setError(response.data.message || 'Failed to load cases.');
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          'Failed to load cases. Please try again later.'
      );
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  // =====================================================
  // FETCH FILTER OPTIONS (PAGINATION-INDEPENDENT)
  // =====================================================

  const fetchFilterOptions = async () => {
    try {
      setFilterOptionsLoading(true);

      const userLabId = getUserLabId();
      if (!userLabId) return;

      const params = { userLabId };

      if (accountId) params.accountId = accountId;

      if (filters.by === 'month') {
        const { startDate, endDate } = getMonthRange(
          currentYear,
          currentMonth
        );
        params.startDate = startDate;
        params.endDate = endDate;
        params.dateField = 'start_date';
      } else if (filters.by === 'dateRange') {
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
        params.dateField = 'date';
      }

      const response = await axios.get(
        `${API_URL}/api/cases/filter-options`,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          params
        }
      );

      if (response.data.success) {
        const opts = response.data.data || {};

        setFilterOptions((prev) => ({
          accounts: mergeOptions(prev.accounts, opts.accounts || []),
          providers: mergeOptions(prev.providers, opts.providers || []),
          patients: mergeOptions(prev.patients, opts.patients || []),
          caseIds: mergeOptions(prev.caseIds, opts.caseIds || []),
          deliveryDates: mergeOptions(
            prev.deliveryDates,
            opts.deliveryDates || []
          ),
          caseTags: mergeStrings(prev.caseTags, opts.caseTags || []),
          statuses: mergeStrings(prev.statuses, opts.statuses || [])
        }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        'filter-options endpoint failed, falling back to page data:',
        err.message
      );

      if (cases.length > 0) {
        const accountSet = new Set();
        const providerSet = new Set();
        const patientSet = new Set();
        const caseIdSet = new Set();
        const deliveryDateSet = new Set();
        const caseTagSet = new Set();
        const statusSet = new Set();

        cases.forEach((c) => {
          if (c.account_name) accountSet.add(c.account_name);
          else if (c.account_holder) accountSet.add(c.account_holder);

          if (c.provider_name) providerSet.add(c.provider_name);
          if (c.last_name) patientSet.add(c.last_name);
          if (c.caseNumber) caseIdSet.add(c.caseNumber);
          if (c.delivery_date)
            deliveryDateSet.add(formatDate(c.delivery_date));
          if (Array.isArray(c.tags))
            c.tags.forEach((t) => caseTagSet.add(t));
          if (c.status) statusSet.add(c.status);
        });

        const toOpts = (s) =>
          Array.from(s)
            .sort((a, b) => String(a).localeCompare(String(b)))
            .map((v) => ({ value: v, label: v }));

        setFilterOptions((prev) => ({
          accounts: mergeOptions(prev.accounts, toOpts(accountSet)),
          providers: mergeOptions(prev.providers, toOpts(providerSet)),
          patients: mergeOptions(prev.patients, toOpts(patientSet)),
          caseIds: mergeOptions(prev.caseIds, toOpts(caseIdSet)),
          deliveryDates: mergeOptions(
            prev.deliveryDates,
            toOpts(deliveryDateSet)
          ),
          caseTags: mergeStrings(prev.caseTags, Array.from(caseTagSet)),
          statuses: mergeStrings(prev.statuses, Array.from(statusSet))
        }));
      }
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  // =====================================================
  // FETCH INVOICED TOTAL
  // =====================================================

  const fetchInvoicedTotal = async () => {
    try {
      const userLabId = getUserLabId();
      if (!userLabId) return;

      const params = {
        userLabId: userLabId,
        amountField: 'total'
      };

      if (accountId) params.accountId = accountId;

      if (filters.by === 'month') {
        const { startDate, endDate } = getMonthRange(
          currentYear,
          currentMonth
        );
        params.startDate = startDate;
        params.endDate = endDate;
      } else if (filters.by === 'dateRange') {
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }

      const response = await axios.get(
        `${API_URL}/api/cases/monthly-total`,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          params: params
        }
      );

      if (response.data.success) {
        const total = Number(response.data.total) || 0;
        setMonthlyInvoicedTotal(total);
      }
    } catch (error) {
      console.error('Error fetching invoiced total:', error);
      setMonthlyInvoicedTotal(0);
    }
  };

  // =====================================================
  // EFFECT: FETCH CASES
  // =====================================================

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    currentPage,
    currentMonth,
    currentYear,
    filters.by,
    filters.startDate,
    filters.endDate,
    filters.filterType,
    filters.filterValue,
    filters.caseTag,
    filters.caseStatus,
    filters.properties
  ]);

  // =====================================================
  // EFFECT: FETCH FILTER OPTIONS
  // =====================================================

  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    currentMonth,
    currentYear,
    filters.by,
    filters.startDate,
    filters.endDate
  ]);

  // =====================================================
  // EFFECT: FETCH INVOICED TOTAL
  // =====================================================

  useEffect(() => {
    fetchInvoicedTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    currentMonth,
    currentYear,
    filters.by,
    filters.startDate,
    filters.endDate
  ]);

  // =====================================================
  // EFFECT: SET MONTHLY TOTAL
  // =====================================================

  useEffect(() => {
    setMonthlyTotal(serverTotalAmount);
  }, [serverTotalAmount]);

  // =====================================================
  // CURRENT FILTER OPTIONS (based on the modal draft)
  // =====================================================

  const getCurrentFilterOptions = () => {
    switch (draftFilters.filterType) {
      case 'account':
        return filterOptions.accounts;
      case 'provider':
        return filterOptions.providers;
      case 'patient':
        return filterOptions.patients;
      case 'caseId':
        return filterOptions.caseIds;
      case 'delivery':
        return filterOptions.deliveryDates;
      default:
        return [];
    }
  };

  // =====================================================
  // PAGINATION HANDLERS
  // =====================================================

  const handleFirstPage = () => setCurrentPage(1);
  const handlePreviousPage = () =>
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handleLastPage = () => setCurrentPage(totalPages);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      let startPage = Math.max(
        1,
        currentPage - Math.floor(maxPagesToShow / 2)
      );
      let endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // =====================================================
  // FILTER HANDLERS
  // =====================================================

  const handleOpenFilterModal = () => {
    setDraftFilters({ ...filters });
    setShowFilterModal(true);
    fetchFilterOptions();
  };

  const handleCloseFilterModal = () => {
    setShowFilterModal(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setDraftFilters((prev) => {
      if (name === 'filterType' && value !== prev.filterType) {
        return { ...prev, filterType: value, filterValue: '' };
      }

      if (name === 'by' && value === 'month') {
        return { ...prev, by: value, startDate: '', endDate: '' };
      }

      return { ...prev, [name]: value };
    });
  };

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters });
    setCurrentPage(1);
    setShowFilterModal(false);

    showSuccessAlert('Filters Applied', 'Cases have been updated.');
  };

  const handleResetFilters = () => {
    setDraftFilters({ ...DEFAULT_FILTERS });
    setFilters({ ...DEFAULT_FILTERS });
    setCurrentPage(1);
    setShowFilterModal(false);

    showInfoAlert('Filters Reset', 'All filters are back to default.');
  };

  // =====================================================
  // NOTES
  // =====================================================

  const handleAddNoteClick = (caseNumber, event) => {
    event.stopPropagation();
    setSelectedCaseId(caseNumber);
    setShowNotesModal(true);
    setDropdownVisible(null);
  };

  const handleNotesUpdate = () => {
    showSuccessAlert(
      'Note Added!',
      'Note has been successfully added to the case.'
    );
  };

  // =====================================================
  // DROPDOWN
  // =====================================================

  const toggleDropdown = (caseId, event) => {
    event.stopPropagation();
    setDropdownVisible(dropdownVisible === caseId ? null : caseId);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      let isOutside = true;

      Object.keys(dropdownMenuRef.current).forEach((key) => {
        if (
          dropdownMenuRef.current[key] &&
          dropdownMenuRef.current[key].contains(event.target)
        ) {
          isOutside = false;
        }
      });

      Object.keys(dropdownRef.current).forEach((key) => {
        if (
          dropdownRef.current[key] &&
          dropdownRef.current[key].contains(event.target)
        ) {
          isOutside = false;
        }
      });

      if (isOutside) setDropdownVisible(null);
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // =====================================================
  // MONTH NAVIGATION
  // =====================================================

  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }

    setCurrentPage(1);
    setDropdownVisible(null);
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }

    setCurrentPage(1);
    setDropdownVisible(null);
  };

  // =====================================================
  // ACCOUNT
  // =====================================================

  const handleAccountClick = (accountName) => {
    const account = accounts.find((acc) => acc.name === accountName);

    if (account) {
      navigate('/TabsWorking', {
        state: {
          accountName: accountName,
          accountData: account
        }
      });
    }
  };

  const displayCases = sortByColumn(cases, sortConfig);

  const monthName = new Date(currentYear, currentMonth).toLocaleString(
    'default',
    { month: 'long' }
  );

  // =====================================================
  // LOADING / ERROR (first load only)
  // =====================================================

  if ((!hasLoadedOnce && loading) || pricingLoading) {
    return (
      <div className="cases-loading">
        <p>Loading cases...</p>
      </div>
    );
  }

  if (error && !hasLoadedOnce) {
    return (
      <div className="cases-error">
        <p>{error}</p>
      </div>
    );
  }

  // =====================================================
  // RETURN
  // =====================================================

  return (
    <>
      <style>{TOAST_CSS}</style>

      <Header />
      <Navvbar />

      {selectedCaseId && (
        <NotesSection
          caseId={selectedCaseId}
          isModalOnly={true}
          showModal={showNotesModal}
          onCloseModal={() => {
            setShowNotesModal(false);
            setSelectedCaseId(null);
          }}
          onNotesUpdate={handleNotesUpdate}
        />
      )}

      <div className="cases-page">
        {/* PAGE HEADER */}
        <div className="cases-header">
          <h2 className="cases-title">
            {accountName
              ? `All Cases for ${accountName}`
              : hasPricingAccess
              ? `Cases Opened ${monthName} ${currentYear} (${formatAmountWithCommas(
                  monthlyTotal
                )})`
              : `Cases Opened ${monthName} ${currentYear}`}
          </h2>

          <div className="month-buttons">
            <button onClick={handlePreviousMonth} className="month-button">
              <MdChevronLeft className="month-icon" />
              Previous Month
            </button>

            <button onClick={handleNextMonth} className="month-button">
              Next Month
              <MdChevronRight className="month-icon" />
            </button>
          </div>
        </div>

        {/* INVOICED HEADER */}
        <div className="invoiced-header">
          <h3 className="invoiced-title">
            {accountName
              ? `All Cases for ${accountName}`
              : hasPricingAccess
              ? `Invoiced in ${monthName} ${currentYear} (${formatAmountWithCommas(
                  monthlyInvoicedTotal
                )})`
              : `Invoiced in ${monthName} ${currentYear}`}
          </h3>
        </div>

        {/* FILTER BUTTON */}
        <div className="filter-button-wrapper">
          <button
            className="filter-button"
            onClick={handleOpenFilterModal}
          >
            <LuFilter />
            Filters
          </button>
        </div>

        {/* Inline error for refetch failures */}
        {error && hasLoadedOnce && (
          <div className="cases-error">
            <p>{error}</p>
          </div>
        )}

        {/* TABLE */}
        <div
          className={
            loading
              ? 'cases-table-wrapper is-refreshing'
              : 'cases-table-wrapper'
          }
        >
          <table className="cases-table">
            <colgroup>
              <col style={{ width: '6%' }} />
              <col style={{ width: '4%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '9%' }} />
            </colgroup>

            <thead>
              <tr>
                <SortHeader
                  label={
                    <span
                      className="icon-tooltip"
                      data-tooltip="Case Status"
                    >
                      <FaBox className="table-icon header-box-icon icon-inbox" />
                    </span>
                  }
                  colKey="status"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <th>
                  <GrAttachment className="table-icon icon-attachment" />
                </th>
                <SortHeader label="Case/Pan" colKey="caseNumber" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Invoice" colKey="invoice" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Account" colKey="account" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Provider" colKey="provider" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Patient/Appt" colKey="patient" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Account Ship" colKey="accountShip" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Lab Ship" colKey="labShip" sortConfig={sortConfig} onSort={handleSort} />
               <SortHeader 
  label="Delivery Date" 
  colKey="delivery" 
  sortConfig={sortConfig} 
  onSort={handleSort} 
/>
              </tr>
            </thead>

            <tbody>
              {displayCases.length > 0 ? (
                displayCases.map((caseItem) => {
                  const invoiceData = caseItem.invoiceNumber
                    ? {
                        invoiceNumber: caseItem.invoiceNumber,
                        invoiceTotal: caseItem.invoiceTotal,
                        caseId: caseItem.caseNumber,
                        invoiceDate: caseItem.invoiceDate
                      }
                    : null;

                  const outsourcedInfo = getOutsourcedStatus(caseItem);
                  const statusInfo = getStatusIcon(caseItem);
                  const StatusIcon = statusInfo.Icon;

                  return (
                    <tr key={caseItem._id} className="case-row">
                      <td className="table-cell status-cell">
                        <span
                          className="icon-tooltip"
                          data-tooltip={statusInfo.tip}
                        >
                          <StatusIcon className={statusInfo.className} />
                        </span>
                      </td>

                      <td className="table-cell">
                        <GrAttachment className="table-icon icon-attachment" />
                      </td>

                      <td className="case-cell">
                        <div className="case-top-row">
                          <Link
                            to={`/case/${caseItem.caseNumber}`}
                            className="case-link"
                          >
                            #{caseItem.caseNumber}
                          </Link>

                          <button
                            ref={(el) =>
                              (dropdownRef.current[caseItem._id] = el)
                            }
                            className="settings-button"
                            onClick={(e) => toggleDropdown(caseItem._id, e)}
                          >
                            <IoSettingsOutline className="settings-icon" />
                          </button>

                          {dropdownVisible === caseItem._id && (
                            <div
                              ref={(el) =>
                                (dropdownMenuRef.current[caseItem._id] = el)
                              }
                              className="case-dropdown"
                            >
                              <div
                                className="dropdown-item"
                                onClick={(e) =>
                                  handleAddNoteClick(caseItem.caseNumber, e)
                                }
                              >
                                <BsPencilFill size={14} />
                                Add Notes
                              </div>

                              <div
                                className="dropdown-item dropdown-item-last"
                                onClick={(e) =>
                                  handleUploadClick(caseItem.caseNumber, e)
                                }
                              >
                                <AiOutlineFileAdd size={14} />
                                Upload Files
                              </div>
                            </div>
                          )}
                        </div>

                        {outsourcedInfo && (
                          <div
                            className="outsourced-status"
                            style={{ color: outsourcedInfo.statusColor }}
                          >
                            → {outsourcedInfo.displayText}
                          </div>
                        )}

                        <div className="pan-color">
                          <span>Pan Color:</span>
                          <span
                            className="pan-color-box"
                            style={{
                              backgroundColor: caseItem.color
                                ? caseItem.color.toLowerCase()
                                : '#ccc'
                            }}
                          />
                          <span>{caseItem.color || 'N/A'}</span>
                        </div>

                        {caseItem.procedures &&
                          caseItem.procedures.length > 0 && (
                            <div className="procedures">
                              {caseItem.procedures
                                .slice(0, 2)
                                .map((proc, idx) => (
                                  <div key={idx}>{proc.procedure}</div>
                                ))}
                              {caseItem.procedures.length > 2 && (
                                <div>
                                  +{caseItem.procedures.length - 2} more
                                </div>
                              )}
                            </div>
                          )}
                      </td>

                      <td className="invoice-cell">
                        {caseItem.invoiceNumber ? (
                          <>
                            <Link
                              to={`/invoices/${formatInvoiceNumber(
                                caseItem.invoiceNumber
                              )}`}
                              state={{
                                invoiceData,
                                caseId: caseItem.caseNumber
                              }}
                              className="invoice-link"
                            >
                              #{formatInvoiceNumber(caseItem.invoiceNumber)}
                            </Link>

                            {caseItem.invoiceDate && (
                              <div className="invoice-date">
                                {formatDate(caseItem.invoiceDate)}
                              </div>
                            )}
                          </>
                        ) : (
                          ''
                        )}

                        {hasPricingAccess && (
                          <div className="invoice-total">
                            {typeof caseItem.totalAmount === 'number'
                              ? formatAmountWithCommas(caseItem.totalAmount)
                              : caseItem.totalPrice &&
                                typeof caseItem.totalPrice === 'number'
                              ? formatAmountWithCommas(caseItem.totalPrice)
                              : null}
                          </div>
                        )}
                      </td>

                      <td className="table-cell account-cell">
                        <span
                          onClick={() =>
                            handleAccountClick(
                              caseItem.account_name || caseItem.account_holder
                            )
                          }
                          className="account-link"
                        >
                          {caseItem.account_name ||
                            caseItem.account_holder ||
                            'N/A'}
                        </span>
                      </td>

                      <td className="table-cell">
                        {caseItem.provider_name || 'N/A'}
                      </td>

                      <td className="table-cell">
                        {caseItem.last_name || 'N/A'}
                      </td>

                      <td className="table-cell">
                        {formatShortDate(caseItem.start_date)}
                      </td>

                      <td className="table-cell">
                        {caseItem.arrive_date
                          ? formatShortDate(caseItem.date)
                          : 'N/A'}
                      </td>

                      <td className="table-cell">
                        {formatShortDate(caseItem.delivery_date)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="no-cases">
                    No cases match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <Button
              onClick={handleFirstPage}
              disabled={currentPage === 1}
              className={
                currentPage === 1
                  ? 'pagination-button disabled'
                  : 'pagination-button'
              }
            >
              First
            </Button>

            <Button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={
                currentPage === 1
                  ? 'pagination-button disabled'
                  : 'pagination-button'
              }
            >
              Previous
            </Button>

            {getPageNumbers().map((pageNumber) => (
              <Button
                key={pageNumber}
                onClick={() => setCurrentPage(pageNumber)}
                className={
                  currentPage === pageNumber
                    ? 'pagination-button active'
                    : 'pagination-button outline'
                }
              >
                {pageNumber}
              </Button>
            ))}

            <Button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={
                currentPage === totalPages
                  ? 'pagination-button disabled'
                  : 'pagination-button'
              }
            >
              Next
            </Button>

            <Button
              onClick={handleLastPage}
              disabled={currentPage === totalPages}
              className={
                currentPage === totalPages
                  ? 'pagination-button disabled'
                  : 'pagination-button'
              }
            >
              Last
            </Button>

            <span className="page-info">
              Page {currentPage} of {totalPages} ({totalCount} cases)
            </span>
          </div>
        )}
      </div>

      {/* FILTER MODAL */}
      <Modal
        show={showFilterModal}
        onHide={handleCloseFilterModal}
        size="lg"
        centered
      >
        <Modal.Header className="filter-modal-header">
          <Modal.Title className="filter-modal-title">
            Change Filters
            {filterOptionsLoading && (
              <span
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginLeft: '10px',
                  fontWeight: '400'
                }}
              >
                (loading options...)
              </span>
            )}
          </Modal.Title>

          <button
            onClick={handleCloseFilterModal}
            className="filter-modal-close"
          >
            <LuX />
          </button>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <div className="filter-modal-content">
              <div className="filter-column">
                <h5 className="filter-section-title">Filters</h5>

                <Form.Group controlId="filterType" className="filter-group">
                  <Form.Label className="filter-label">Filter By</Form.Label>
                  <Form.Select
                    name="filterType"
                    value={draftFilters.filterType}
                    onChange={handleFilterChange}
                    className="filter-select"
                  >
                    <option value="account">Account</option>
                    <option value="provider">Provider</option>
                    <option value="patient">Patient</option>
                    <option value="caseId">Case ID</option>
                    <option value="delivery">Delivery Date</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterValue" className="filter-group">
                  <Form.Label className="filter-label">
                    {draftFilters.filterType === 'account'
                      ? 'Account'
                      : draftFilters.filterType === 'provider'
                      ? 'Provider'
                      : draftFilters.filterType === 'patient'
                      ? 'Patient'
                      : draftFilters.filterType === 'caseId'
                      ? 'Case ID'
                      : 'Delivery Date'}
                  </Form.Label>

                  <Form.Select
                    name="filterValue"
                    value={draftFilters.filterValue}
                    onChange={handleFilterChange}
                    className="filter-select"
                  >
                    <option value="">
                      All{' '}
                      {draftFilters.filterType === 'account'
                        ? 'Accounts'
                        : draftFilters.filterType === 'provider'
                        ? 'Providers'
                        : draftFilters.filterType === 'patient'
                        ? 'Patients'
                        : draftFilters.filterType === 'caseId'
                        ? 'Case IDs'
                        : 'Delivery Dates'}
                    </option>

                    {getCurrentFilterOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterBy" className="filter-group">
                  <Form.Label className="filter-label">Date Range</Form.Label>
                  <Form.Select
                    name="by"
                    value={draftFilters.by}
                    onChange={handleFilterChange}
                    className="filter-select"
                  >
                    <option value="month">Current Month</option>
                    <option value="dateRange">Custom Date Range</option>
                  </Form.Select>
                </Form.Group>

                {draftFilters.by === 'dateRange' && (
                  <>
                    <Form.Group
                      controlId="filterStartDate"
                      className="filter-group"
                    >
                      <Form.Label className="filter-label">
                        Start Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="startDate"
                        value={draftFilters.startDate}
                        onChange={handleFilterChange}
                        className="filter-select"
                      />
                    </Form.Group>

                    <Form.Group
                      controlId="filterEndDate"
                      className="filter-group-end"
                    >
                      <Form.Label className="filter-label">
                        End Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="endDate"
                        value={draftFilters.endDate}
                        onChange={handleFilterChange}
                        className="filter-select"
                      />
                    </Form.Group>
                  </>
                )}
              </div>

              <div className="filter-column">
                <h5 className="filter-section-title">
                  Case Status Options
                </h5>

                <Form.Group controlId="filterCaseTag" className="filter-group">
                  <Form.Label className="filter-label">Case Tag</Form.Label>
                  <Form.Select
                    name="caseTag"
                    value={draftFilters.caseTag}
                    onChange={handleFilterChange}
                    className="filter-select"
                  >
                    <option value="any">Any</option>
                    {filterOptions.caseTags.map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group
                  controlId="filterCaseStatus"
                  className="filter-group"
                >
                  <Form.Label className="filter-label">Case Status</Form.Label>
                  <Form.Select
                    name="caseStatus"
                    value={draftFilters.caseStatus}
                    onChange={handleFilterChange}
                    className="filter-select"
                  >
                    <option value="">All Statuses</option>
                    {filterOptions.statuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group
                  controlId="filterProperties"
                  className="filter-group"
                >
                  <Form.Label className="filter-label">Properties</Form.Label>
                  <Form.Select
                    name="properties"
                    value={draftFilters.properties}
                    onChange={handleFilterChange}
                    className="filter-select"
                  >
                    <option value="all">All</option>
                    <option value="digital">
                      Digital Manufacturing Files
                    </option>
                    <option value="enclosures">Enclosures</option>
                    <option value="both">Digital + Enclosures</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer className="filter-modal-footer">
          <Button
            variant="secondary"
            onClick={handleResetFilters}
            className="reset-filter-button"
          >
            Reset
          </Button>

          <Button
            variant="primary"
            onClick={handleApplyFilters}
            className="apply-filter-button"
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CasesOpened;